<template>
  <q-page class="first-login-page">
    <div class="first-login-card">
      <!-- Screen 5 - reset impossible (company listed in COMPANY_RESET_PASSWORD_FORBIDDEN) -->
      <template v-if="restricted">
        <div class="header-section">
          <div class="restricted-badge">
            <q-icon name="lock" size="24px" color="white" />
          </div>
          <h1 class="title">{{ $t('login.magicLinkForbiddenTitle') }}</h1>
          <p class="subtitle">{{ $t('login.magicLinkForbiddenMessage') }}</p>
        </div>
        <q-btn
          no-caps
          unelevated
          class="submit-button"
          data-cy="restricted-back"
          :label="$t('login.goBack')"
          @click="goBackToLogin"
        />
      </template>

      <!-- Screen 1 - email -->
      <template v-else-if="step === 'email'">
        <div class="header-section">
          <div class="icon-badge">
            <q-icon name="verified_user" size="28px" />
          </div>
          <h1 class="title">
            {{
              isActivateContext
                ? $t('login.firstConnectionTitle')
                : $t('login.forgotPasswordTitle')
            }}
          </h1>
          <p class="subtitle">
            {{
              isActivateContext
                ? $t('login.firstConnectionDescription')
                : $t('login.forgotPasswordDescription')
            }}
          </p>
        </div>

        <form class="first-login-form" @submit.prevent="requestChannels">
          <div class="field">
            <label class="field-label" for="first-login-email">
              {{ $t('login.email') }}
            </label>
            <q-input
              id="first-login-email"
              v-model.trim="email"
              type="email"
              outlined
              hide-bottom-space
              autocomplete="username"
              data-cy="first-login-email"
              class="field-input"
              :placeholder="$t('login.emailPlaceholder')"
              :error="!!emailError"
              :error-message="emailError"
            >
              <template #prepend>
                <q-icon name="mail_outline" />
              </template>
            </q-input>
          </div>

          <div
            v-if="apiErrorMessage"
            class="server-error"
            data-cy="server-error"
          >
            <q-icon
              name="error_outline"
              size="18px"
              class="server-error-icon"
            />
            <span>{{ apiErrorMessage }}</span>
          </div>

          <q-btn
            type="submit"
            no-caps
            unelevated
            class="submit-button"
            data-cy="first-login-continue"
            :label="$t('login.continueButton')"
            :loading="isLoading"
          />
        </form>
      </template>

      <!-- Screen 2 - channel -->
      <template v-else-if="step === 'channel'">
        <div class="header-section">
          <div class="icon-badge">
            <q-icon name="lock_outline" size="28px" />
          </div>
          <h1 class="title">{{ $t('login.channelTitle') }}</h1>
          <p class="subtitle">{{ $t('login.channelSubtitle') }}</p>
        </div>

        <div class="channel-options">
          <div
            class="channel-option"
            :class="{ 'channel-option--selected': selectedChannel === 'email' }"
            data-cy="channel-email"
            @click="selectChannel('email')"
          >
            <div class="channel-icon-box">
              <q-icon name="mail_outline" size="22px" />
            </div>
            <div class="channel-text">
              <div class="channel-label">
                {{ $t('login.channelEmailLabel') }}
              </div>
              <div class="channel-description">{{ channelHint('email') }}</div>
            </div>
            <div
              class="channel-radio"
              :class="{
                'channel-radio--selected': selectedChannel === 'email'
              }"
            >
              <div
                v-if="selectedChannel === 'email'"
                class="channel-radio-dot"
              />
            </div>
          </div>

          <div
            v-if="channels.includes('sms')"
            class="channel-option"
            :class="{ 'channel-option--selected': selectedChannel === 'sms' }"
            data-cy="channel-sms"
            @click="selectChannel('sms')"
          >
            <div class="channel-icon-box">
              <q-icon name="sms" size="22px" />
            </div>
            <div class="channel-text">
              <div class="channel-label">{{ $t('login.channelSmsLabel') }}</div>
              <div class="channel-description">{{ channelHint('sms') }}</div>
            </div>
            <div
              class="channel-radio"
              :class="{ 'channel-radio--selected': selectedChannel === 'sms' }"
            >
              <div v-if="selectedChannel === 'sms'" class="channel-radio-dot" />
            </div>
          </div>
        </div>

        <div v-if="apiErrorMessage" class="server-error" data-cy="server-error">
          <q-icon name="error_outline" size="18px" class="server-error-icon" />
          <span>{{ apiErrorMessage }}</span>
        </div>

        <q-btn
          no-caps
          unelevated
          class="submit-button"
          data-cy="send-magic-link"
          :label="$t('login.sendMagicLink')"
          :loading="isLoading"
          @click="sendMagicLink()"
        />

        <div class="banner banner--warning">
          <q-icon name="schedule" size="18px" class="banner-icon" />
          <span>{{ $t('login.channelExpiryWarning') }}</span>
        </div>
      </template>

      <!-- Screen 3 - link sent -->
      <template v-else>
        <div class="header-section">
          <div class="success-icon-outer">
            <div class="success-icon-inner">
              <q-icon name="check" size="24px" color="white" />
            </div>
          </div>
          <h1 class="title">{{ $t('login.magicLinkSentTitle') }}</h1>
          <p class="subtitle">{{ $t('login.magicLinkSentDescription') }}</p>
          <p class="subtitle-secondary" data-cy="magic-link-check-message">
            {{ checkChannelMessage }}
          </p>
        </div>

        <q-btn
          v-if="!isSmsChannel"
          no-caps
          unelevated
          icon="mail_outline"
          class="submit-button"
          data-cy="open-mailbox"
          :label="$t('login.openMailbox')"
          @click="openMailbox"
        />

        <q-btn
          outline
          no-caps
          class="resend-button"
          data-cy="resend-magic-link"
          :label="
            resendCountdown > 0
              ? $t('login.resendMagicLinkCountdown', {
                  seconds: resendCountdown
                })
              : $t('login.resendMagicLink')
          "
          :disable="resendCountdown > 0 || isLoading"
          :loading="isLoading"
          @click="resend"
        />

        <div class="banner banner--info">
          <q-icon name="info_outline" size="18px" class="banner-icon" />
          <span>{{ $t('login.magicLinkWarning') }}</span>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMagicLink } from 'src/composables/useMagicLink'

export default {
  name: 'FirstLogin',
  setup() {
    const { t } = useI18n()
    const magicLink = useMagicLink()

    const channelHint = (channel) => {
      const target = magicLink.channelTargets.value[channel]
      if (target) return target
      return channel === 'email'
        ? magicLink.maskedEmail.value
        : t('login.channelSmsDescription')
    }

    const isSmsChannel = computed(
      () => magicLink.selectedChannel.value === 'sms'
    )

    const checkChannelMessage = computed(() =>
      isSmsChannel.value
        ? t('login.magicLinkCheckSmsMessage')
        : t('login.magicLinkCheckInboxMessage')
    )

    return {
      ...magicLink,
      channelHint,
      isSmsChannel,
      checkChannelMessage
    }
  }
}
</script>

<style lang="scss" scoped>
.first-login-page {
  background: white;
  display: flex;
  flex-direction: column;
}

.first-login-card {
  flex: 1;
  padding: 24px 20px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.header-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

.icon-badge {
  width: 66px;
  height: 66px;
  border-radius: 9999px;
  background: rgba(64, 196, 255, 0.15);
  color: #40c4ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.restricted-badge {
  width: 66px;
  height: 66px;
  border-radius: 9999px;
  background: #ffce6e;
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-icon-outer {
  width: 96px;
  height: 96px;
  border-radius: 9999px;
  background: rgba(6, 214, 160, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-icon-inner {
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  background: #06d6a0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3em;
  color: #152f3b;
  margin: 0;
}

.subtitle {
  font-size: 15px;
  font-weight: 400;
  line-height: 1.4em;
  color: #3e484f;
  margin: 0;
}

.subtitle-secondary {
  font-size: 14px;
  line-height: 1.4em;
  color: rgba(62, 72, 79, 0.8);
  margin: 0;
}

.first-login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 15px;
  font-weight: 600;
  color: #152f3b;
}

.field-input :deep(.q-field__control) {
  height: 52px;
  border-radius: 10px;
}

.field-input :deep(.q-field__marginal) {
  height: 52px;
}

.server-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: rgba(239, 71, 111, 0.1);
  border-radius: 8px;
  padding: 12px;
  color: #ef476f;
  font-size: 13px;
  line-height: 1.35em;
}

.server-error-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.submit-button {
  width: 100%;
  height: 54px;
  background: #40c4ff;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border-radius: 15px;
}

.resend-button {
  width: 100%;
  height: 54px;
  font-size: 15px;
  font-weight: 600;
  color: #414651;
  border-radius: 15px;
}

.channel-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.channel-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #bdc8d1;
  cursor: pointer;
}

.channel-option--selected {
  border-color: #40c4ff;
  background: #f3fcff;
}

.channel-icon-box {
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 8px;
  background: #cff0ff;
  color: #00668a;
  display: flex;
  align-items: center;
  justify-content: center;
}

.channel-text {
  flex: 1;
  min-width: 0;
}

.channel-label {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.channel-description {
  font-size: 14px;
  color: #64748b;
  margin-top: 2px;
  overflow-wrap: anywhere;
}

.channel-radio {
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 9999px;
  border: 1px solid #bdc8d1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.channel-radio--selected {
  background: #40c4ff;
  border-color: #40c4ff;
}

.channel-radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: white;
}

.banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.35em;
  color: #607680;
  margin-top: auto;
}

.banner--warning,
.banner--info {
  background: rgba(255, 189, 61, 0.15);
  border-left: 4px solid #ffce6e;
}

.banner-icon {
  flex-shrink: 0;
  color: #f09e00;
}
</style>
